import { useCallback, useEffect, useRef, useState } from "react";

/** Keep floating popups below the control top bar (`h-topBar` = 5rem). */
const TOP_BAR_CLEARANCE_PX = 88;
const MARGIN_PX = 16;
const DRAG_MARGIN_PX = 8;
const MIN_W = 280;
const MIN_H = 200;

type Geometry = { x: number; y: number; w: number; h: number };

type Options = {
    /** Prefix for the localStorage keys holding geometry and collapsed state. */
    storageKey: string;
    defaultWidth: number;
    defaultHeight: number;
    /** Place the default position on the right edge instead of the left. */
    anchorRight?: boolean;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const readGeometry = ({ storageKey, defaultWidth, defaultHeight, anchorRight }: Options): Geometry => {
    const fallback: Geometry = {
        x: anchorRight ? Math.max(MARGIN_PX, window.innerWidth - defaultWidth - MARGIN_PX) : MARGIN_PX,
        y: TOP_BAR_CLEARANCE_PX,
        w: defaultWidth,
        h: defaultHeight,
    };
    try {
        const raw = window.localStorage.getItem(`${storageKey}-geometry`);
        if (!raw) return fallback;
        const parsed = JSON.parse(raw) as Partial<Geometry>;
        const w = clamp(Number(parsed.w) || defaultWidth, MIN_W, window.innerWidth - MARGIN_PX * 2);
        const h = clamp(Number(parsed.h) || defaultHeight, MIN_H, window.innerHeight - MARGIN_PX * 2);
        return {
            w,
            h,
            x: clamp(Number(parsed.x) || MARGIN_PX, MARGIN_PX, Math.max(MARGIN_PX, window.innerWidth - w - MARGIN_PX)),
            y: clamp(
                Number(parsed.y) || TOP_BAR_CLEARANCE_PX,
                TOP_BAR_CLEARANCE_PX,
                Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - MARGIN_PX),
            ),
        };
    } catch {
        return fallback;
    }
};

/** Persisted position, size and collapsed state for a draggable, resizable popup. */
export const useFloatingPopup = (options: Options) => {
    const { storageKey } = options;
    const [geometry, setGeometry] = useState<Geometry>(() => readGeometry(options));
    const [collapsed, setCollapsed] = useState(
        () => window.localStorage.getItem(`${storageKey}-collapsed`) === "true",
    );
    const panelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        window.localStorage.setItem(`${storageKey}-geometry`, JSON.stringify(geometry));
    }, [geometry, storageKey]);

    useEffect(() => {
        window.localStorage.setItem(`${storageKey}-collapsed`, String(collapsed));
    }, [collapsed, storageKey]);

    const trackPointer = (onMove: (event: PointerEvent) => void, onEnd?: () => void) => {
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            window.removeEventListener("pointercancel", stop);
            onEnd?.();
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        window.addEventListener("pointercancel", stop);
    };

    const onDragHandlePointerDown = useCallback(
        (event: React.PointerEvent<HTMLElement>) => {
            if (event.button !== 0) return;
            // Controls inside the header stay clickable; drag from the rest.
            if ((event.target as Element | null)?.closest?.("button, input, textarea, a, [role='tab']")) return;
            event.preventDefault();
            const origin = { x: geometry.x, y: geometry.y };
            const start = { x: event.clientX, y: event.clientY };
            trackPointer((move) => {
                const w = panelRef.current?.offsetWidth ?? geometry.w;
                const h = panelRef.current?.offsetHeight ?? geometry.h;
                setGeometry((prev) => ({
                    ...prev,
                    x: clamp(origin.x + move.clientX - start.x, DRAG_MARGIN_PX, Math.max(DRAG_MARGIN_PX, window.innerWidth - w - DRAG_MARGIN_PX)),
                    y: clamp(origin.y + move.clientY - start.y, TOP_BAR_CLEARANCE_PX, Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - DRAG_MARGIN_PX)),
                }));
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [geometry.x, geometry.y, geometry.w, geometry.h],
    );

    const onResizePointerDown = useCallback(
        (event: React.PointerEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const target = event.currentTarget;
            target.setPointerCapture(event.pointerId);
            const start = { x: event.clientX, y: event.clientY, w: geometry.w, h: geometry.h };
            trackPointer(
                (move) => {
                    const w = clamp(start.w + move.clientX - start.x, MIN_W, Math.round(window.innerWidth * 0.92));
                    const h = clamp(start.h + move.clientY - start.y, MIN_H, Math.round(window.innerHeight * 0.8));
                    setGeometry((prev) => ({
                        w,
                        h,
                        x: clamp(prev.x, DRAG_MARGIN_PX, Math.max(DRAG_MARGIN_PX, window.innerWidth - w - DRAG_MARGIN_PX)),
                        y: clamp(prev.y, TOP_BAR_CLEARANCE_PX, Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - DRAG_MARGIN_PX)),
                    }));
                },
                () => {
                    try {
                        target.releasePointerCapture(event.pointerId);
                    } catch {
                        /* pointer already released */
                    }
                },
            );
        },
        [geometry.w, geometry.h],
    );

    return {
        geometry,
        collapsed,
        toggleCollapsed: () => setCollapsed((prev) => !prev),
        panelRef,
        onDragHandlePointerDown,
        onResizePointerDown,
    };
};
